// Detecting a mini-app launch, and holding on to the credential that proves it.
//
// Bale (like Telegram) appends the signed launch payload to the URL fragment:
//
//   https://pwa.hamyanapp.com/#tgWebAppData=<urlencoded>&tgWebAppPlatform=web&…
//
// `miniapp.js` does nothing more magical than read `location.hash` — verified
// against the SDK source — so we parse it ourselves. That keeps auto-login
// working even when tapi.bale.ai is slow or blocked, and lets us capture the
// payload before React Router ever touches the URL.

export type MiniappPlatform = 'bale' | 'telegram' | 'eitaa'

const INIT_DATA_KEY = 'miniapp:init_data'
const PLATFORM_KEY = 'miniapp:platform'
const OPTED_OUT_KEY = 'miniapp:opted_out'
const LAUNCH_HASH_KEY = 'miniapp:launch_hash'
const LINK_TICKET_KEY = 'miniapp:link_ticket'

const hashParams = (): URLSearchParams =>
    new URLSearchParams(window.location.hash.replace(/^#/, ''))

/**
 * The raw initData blob for this launch, or null when we are an ordinary web page.
 *
 * Read from the fragment on the first call of a session and remembered
 * afterwards: the axios 401 interceptor navigates with `location.href`, which
 * drops the fragment, and the cold-start flow still needs the blob after the
 * user finishes the SMS login.
 */
export const getInitData = (): string | null => {
    const fromHash = hashParams().get('tgWebAppData')
    if (fromHash) {
        try {
            // Keep the WHOLE fragment, not just our own field. The messenger
            // SDKs re-parse `location.hash` themselves whenever they load, and
            // by then routing has usually replaced the URL and dropped it —
            // leaving them with no version, no platform and no theme, which
            // makes their version-gated methods throw. See restoreLaunchHash().
            window.sessionStorage.setItem(
                LAUNCH_HASH_KEY,
                window.location.hash.replace(/^#/, '')
            )
        } catch {
            /* best effort */
        }
        try {
            // sessionStorage, not localStorage: this is a bearer credential and
            // it should die with the tab rather than outlive the mini app.
            window.sessionStorage.setItem(INIT_DATA_KEY, fromHash)
        } catch {
            // Private-mode / storage-disabled webviews still auto-login; only
            // the post-OTP link step is lost, and the user can re-open the app.
        }
        return fromHash
    }

    try {
        return window.sessionStorage.getItem(INIT_DATA_KEY)
    } catch {
        return null
    }
}

/** True when this page load is (or continues) a mini-app session. */
export const isMiniapp = (): boolean => getInitData() !== null

/**
 * Mark the document so CSS can tell it is embedded in a messenger.
 *
 * Drives `--safe-bottom` (see index.scss): Bale's webview reports a bottom
 * inset it does not actually need, and honouring it mis-sizes the bottom nav.
 * Set before the first paint so nothing renders at the wrong height first.
 */
export const markMiniappDocument = () => {
    if (!isMiniapp()) return
    document.documentElement.setAttribute('data-miniapp', 'bale')
}

/**
 * Which client we are embedded in. `tgWebAppPlatform === 'web'` means the
 * desktop/web client, which renders us in an iframe; anything else is a native
 * webview. Absent once we are running off the stored blob.
 */
export const getClientPlatform = (): string | null =>
    hashParams().get('tgWebAppPlatform')

/**
 * Optional platform hint from the registered mini-app URL (`?mp=telegram`).
 *
 * Purely a speed-up: on a first launch we would otherwise not know which SDK
 * to load until the auth round-trip answers, leaving the messenger's own
 * loading spinner up for that extra beat. With the hint we can call ready()
 * immediately.
 *
 * It is NOT trusted for anything else. Authentication is decided by which
 * secret validates the payload signature, so putting `?mp=telegram` on a Bale
 * launch changes which SDK script loads and nothing whatsoever about identity.
 */
export const getPlatformHint = (): MiniappPlatform | null => {
    const hint = new URLSearchParams(window.location.search).get('mp')
    return hint === 'bale' || hint === 'telegram' || hint === 'eitaa'
        ? hint
        : null
}

/** Deep-link payload from `ble.ir/hamyan_minibot?startapp=<value>`. */
export const getStartParam = (): string | null =>
    // Query string, not the fragment — Bale's SDK reads this one off
    // `location.search`, unlike Telegram. Checked against the SDK source.
    new URLSearchParams(window.location.search).get('tgWebAppStartParam')

/**
 * Remember which messenger the backend identified.
 *
 * Kept so a later launch that short-circuits on an existing session (and so
 * never calls auth again) still knows which SDK to load for ready()/fullscreen.
 */
export const rememberPlatform = (platform: MiniappPlatform) => {
    try {
        window.sessionStorage.setItem(PLATFORM_KEY, platform)
    } catch {
        /* best effort */
    }
}

export const getRememberedPlatform = (): MiniappPlatform | null => {
    try {
        const stored = window.sessionStorage.getItem(PLATFORM_KEY)
        return stored === 'bale' || stored === 'telegram' || stored === 'eitaa'
            ? stored
            : null
    } catch {
        return null
    }
}

/**
 * Hold the launch proof until the SMS login finishes.
 *
 * sessionStorage for the same reason as initData: it is a credential and
 * should die with the tab.
 */
export const rememberLinkTicket = (ticket: string) => {
    try {
        window.sessionStorage.setItem(LINK_TICKET_KEY, ticket)
    } catch {
        /* best effort — link falls back to re-sending initData */
    }
}

export const getLinkTicket = (): string | null => {
    try {
        return window.sessionStorage.getItem(LINK_TICKET_KEY)
    } catch {
        return null
    }
}

/**
 * A safe description of this launch, for analytics.
 *
 * Deliberately excludes `hash` and the raw blob. initData is a bearer
 * credential inside its freshness window — anyone who can read it can mint a
 * session for that user — and analytics is a third party with its own access
 * list and retention. Everything here is metadata *about* the launch: enough
 * to answer "which messenger, which client, which version, what did it send",
 * without shipping the thing that grants access.
 */
export const describeLaunch = (): Record<string, unknown> => {
    const hash = hashParams()
    const initData = getInitData()

    let fields: string[] = []
    let user: Record<string, unknown> = {}
    if (initData) {
        try {
            const parsed = new URLSearchParams(decodeURIComponent(initData))
            fields = Array.from(parsed.keys()).sort()
            const rawUser = parsed.get('user')
            if (rawUser) {
                const u = JSON.parse(rawUser)
                user = {
                    // The messenger's own id — not a secret, and the only way
                    // to correlate a launch with a linked account.
                    miniapp_user_id: String(u?.id ?? ''),
                    miniapp_has_username: !!u?.username,
                    miniapp_has_photo: !!u?.photo_url,
                    miniapp_has_last_name: !!u?.last_name,
                }
            }
        } catch {
            fields = ['<unparseable>']
        }
    }

    return {
        // `tgWebAppVersion` is the one that decides whether the SDK will even
        // run its gated methods — requestContact throws when it reads too low.
        miniapp_client: hash.get('tgWebAppPlatform') || null,
        miniapp_sdk_version: hash.get('tgWebAppVersion') || null,
        miniapp_has_theme: !!hash.get('tgWebAppThemeParams'),
        miniapp_start_param: getStartParam(),
        miniapp_initdata_fields: fields,
        miniapp_initdata_present: !!initData,
        ...user,
    }
}

/**
 * Put the launch fragment back on the URL if routing has dropped it.
 *
 * Call immediately before loading a messenger SDK. The SDKs read
 * `location.hash` once, at load, and derive their reported version from
 * `tgWebAppVersion`; with the fragment gone they fall back to "0.0" and refuse
 * every version-gated method — which is exactly why Eitaa's requestContact
 * threw WebAppMethodUnsupported on a client that reports 1.6.
 *
 * Restoring is not a new exposure: this is the same fragment the messenger
 * itself put in the address to begin with.
 */
export const restoreLaunchHash = () => {
    try {
        if (hashParams().get('tgWebAppData')) return
        const saved = window.sessionStorage.getItem(LAUNCH_HASH_KEY)
        if (!saved) return
        window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search + '#' + saved
        )
    } catch {
        /* best effort — the SDK simply keeps its degraded view */
    }
}

export const clearStoredInitData = () => {
    try {
        window.sessionStorage.removeItem(INIT_DATA_KEY)
        window.sessionStorage.removeItem(PLATFORM_KEY)
    } catch {
        /* nothing to clear */
    }
}

/**
 * Stop auto-login from undoing a deliberate logout.
 *
 * The launch payload stays valid for hours and lives in the URL fragment, so
 * without this flag tapping "خروج" would sign the user straight back in on the
 * next render. Kept in sessionStorage so it survives `localStorage.clear()` in
 * logout() but dies with the tab — re-opening the mini app is a fresh start.
 */
export const suppressAutoLogin = () => {
    try {
        window.sessionStorage.setItem(OPTED_OUT_KEY, '1')
    } catch {
        /* best effort */
    }
}

export const isAutoLoginSuppressed = (): boolean => {
    try {
        return window.sessionStorage.getItem(OPTED_OUT_KEY) === '1'
    } catch {
        return false
    }
}
